import { Reveal, Overline } from "./Reveal";
import { Check, MapPin, Clock, AlertCircle } from "lucide-react";

const scrollToApply = (interest) => {
  const el = document.getElementById("apply");
  if (el) {
    el.scrollIntoView({ behavior: "smooth" });
    window.dispatchEvent(new CustomEvent("prefill-program", { detail: interest }));
  }
};

const receive = [
  "One-on-one coaching",
  "Customized workout programming",
  "Exercise technique & form correction",
  "Accountability and motivation",
  "Nutrition guidance",
  "Progress tracking",
  "Personalized coaching for all experience levels",
];

const packages = [
  { name: "Single Session", price: "$75", per: "", sub: "60-minute session", popular: false },
  { name: "Starter Package", price: "$260", per: "/mo", sub: "1 session per week (4 sessions)", popular: false },
  { name: "Transformation Package", price: "$480", per: "/mo", sub: "2 sessions per week (8 sessions)", popular: true },
  { name: "Elite Package", price: "$690", per: "/mo", sub: "3 sessions per week (12 sessions)", popular: false },
  { name: "Premium Transformation", price: "$850", per: "/mo", sub: "4 sessions per week (16 sessions)", popular: false },
];

export const InPersonTraining = () => {
  return (
    <section id="in-person" className="relative py-24 md:py-32 bg-[#141414]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="max-w-2xl">
          <Overline>In-Person Personal Training</Overline>
          <h2 className="font-display font-semibold uppercase tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight">
            One-On-One. In The Gym. All In.
          </h2>
          <p className="mt-5 text-[#A3A3A3] leading-relaxed">
            Experience personalized one-on-one coaching designed specifically around your goals.
            Whether it&rsquo;s fat loss, muscle growth, strength, athletic performance, or simply building
            confidence in the gym, every session is tailored to your individual needs.
          </p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full bg-[#0A0A0A] border border-[#262626] p-8">
              <h3 className="font-display font-semibold text-xl text-white">What You&rsquo;ll Receive</h3>
              <ul className="mt-6 space-y-3">
                {receive.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-white/90">
                    <Check size={16} className="text-[#0055FF] mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="h-full flex flex-col gap-6">
              <div className="bg-[#0A0A0A] border border-[#262626] p-8">
                <div className="flex items-center gap-2 text-[#0055FF] mb-4">
                  <MapPin size={18} />
                  <h3 className="font-display font-semibold text-xl text-white">Location & Availability</h3>
                </div>
                <p className="text-sm text-[#A3A3A3] leading-relaxed">
                  In-person training is available exclusively for local clients. All sessions take place
                  at my designated training gym, by appointment only, based on availability. A valid
                  membership to the training gym is required before beginning — clients are responsible
                  for obtaining and maintaining their own gym membership. I accept only a limited number
                  of in-person clients at a time.
                </p>
              </div>
              <div className="bg-[#0A0A0A] border border-[#262626] p-8">
                <div className="flex items-center gap-2 text-[#0055FF] mb-4">
                  <Clock size={18} />
                  <h3 className="font-display font-semibold text-xl text-white">Scheduling & Cancellation</h3>
                </div>
                <ul className="space-y-2 text-sm text-[#A3A3A3] leading-relaxed">
                  <li>• Sessions are scheduled in advance based on availability.</li>
                  <li>• Minimum 24 hours&rsquo; notice required to cancel or reschedule.</li>
                  <li>• Missed sessions without proper notice are forfeited.</li>
                  <li>• Arriving 15+ minutes late may shorten or forfeit the session.</li>
                </ul>
              </div>
            </div>
          </Reveal>
        </div>

        <h3 className="mt-16 text-sm uppercase tracking-[0.2em] text-[#A3A3A3] font-bold mb-6">Pricing</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {packages.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.06}>
              <div
                data-testid={`inperson-card-${i}`}
                className={`relative h-full flex flex-col p-6 ${
                  p.popular
                    ? "bg-[#0A0A0A] border-2 border-[#0055FF] shadow-[0_0_40px_rgba(0,85,255,0.18)]"
                    : "bg-[#0A0A0A] border border-[#262626]"
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-3 left-4 bg-[#0055FF] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1">
                    Most Popular
                  </span>
                )}
                <h4 className="font-display font-semibold text-base text-white leading-tight min-h-[2.5rem]">{p.name}</h4>
                <div className="mt-3 flex items-end gap-0.5">
                  <span className="font-display font-bold text-3xl text-white">{p.price}</span>
                  {p.per && <span className="text-[#A3A3A3] mb-1 text-sm">{p.per}</span>}
                </div>
                <p className="mt-2 text-xs text-[#A3A3A3] leading-relaxed flex-1">{p.sub}</p>
                <button
                  data-testid={`inperson-apply-${i}`}
                  onClick={() => scrollToApply(`In-Person: ${p.name}`)}
                  className={`mt-5 w-full font-bold px-4 py-3 uppercase tracking-wide text-xs transition-colors ${
                    p.popular ? "bg-[#0055FF] text-white hover:bg-[#3377FF]" : "bg-transparent border border-[#262626] text-white hover:border-[#0055FF]"
                  }`}
                >
                  Apply
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 bg-[#0A0A0A] border border-[#262626] p-8 sm:p-10 text-center">
            <AlertCircle size={28} className="text-[#0055FF] mx-auto" />
            <h3 className="font-display font-semibold text-2xl text-white mt-4 uppercase tracking-tight">Ready To Train In Person?</h3>
            <p className="mt-3 text-[#A3A3A3] max-w-xl mx-auto">
              If you&rsquo;re looking for personalized coaching, accountability, and professional guidance,
              apply today to reserve your spot. In-person availability is limited and offered on a
              first-come, first-served basis.
            </p>
            <button
              data-testid="inperson-cta-apply"
              onClick={() => scrollToApply("In-Person Personal Training")}
              className="mt-6 bg-[#0055FF] text-white hover:bg-[#3377FF] transition-colors font-bold px-8 py-4 uppercase tracking-wide"
            >
              Reserve Your Spot
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
